import * as React from "react"
import { useState, useEffect } from "react"
import { Box, Button, Card, CardContent, Grid, Tabs, Tab, Typography } from "@mui/material"
import { AutocompleteAjaxSeeker, IconSeeker } from "@ui-seeker/core"

import { BACKEND_APIS, RETAILER_BRANDS_ROUTES } from "@constants"
import PageWrapper from "Pages/@PageWrapper"
// Reuse the master-data brand creation popup (Drawer controlled by its formData signal),
// exactly like the dipi dashboard does
import AdminBrandsForm from "../Brands/Brand/Drawer"
import { formData as brandFormData } from "../Brands/Brand/@signals"
import generateHexColor from "../Brands/Brand/@utils"
import ProductBrandsPanel from "./ProductBrandsPanel"
import VisibilityBrandsPanel from "./VisibilityBrandsPanel"

const idOf = (v) => (v && typeof v === 'object' ? v.value : v)

/**
 * This dashboard on the PUIG deployment (see Routes.jsx). Same page and same route as
 * on dipi, with the two tabs swapped for the ones that apply here — Puig has no
 * RETAILER_BRAND / DIPI_LAST_ITEM_RAW, so "Retailer brands" and "Competitor brands"
 * have nothing to read.
 *
 * - Product: raw brand from LAST_RAW (CH) -> DIPI_SKU_MAPPING.id_brand, per site.
 * - Visibility: raw brand from ITEM_VISIBILITY_RAW (CH) -> DIPI_BRAND_MAPPING, global.
 *
 * The Customer selector lives here and is shared by both tabs, like dipi's page does
 * with its own. It matters: 50 of the 918 Puig sites carry data from more than one
 * customer (Puig Derma has 139 sites, Beauty And Fashion 828).
 */
export default function PuigBrandsPage() {
    const [tab, setTab] = useState(0)
    const [customer, setCustomer] = useState(null)
    // Shared by both tabs, like the customer above. It has to live here: the panels sit
    // in the same slot with a different `key`, so a tab switch unmounts one of them and
    // a site held inside it dies with the fiber — which is what read as "the filter
    // resets itself" (CU-869f0mg55).
    const [site, setSite] = useState(null)

    const cid = idOf(customer)

    // Both site catalogs are the customer's, so a customer change invalidates the pick.
    useEffect(() => { setSite(null) }, [cid])

    return (
        <PageWrapper
            head={RETAILER_BRANDS_ROUTES.TITLE}
            title={RETAILER_BRANDS_ROUTES.TITLE}
            subtitle={RETAILER_BRANDS_ROUTES.SUBTITLE}
            actions={null}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Box sx={{ minWidth: 280 }}>
                                <AutocompleteAjaxSeeker
                                    urlAjax={`${BACKEND_APIS.BATCH_ADMIN}/customers`}
                                    filters={{ clientType: 'brand' }}
                                    label="Customer"
                                    placeholder="Select a customer…"
                                    value={customer}
                                    onChange={e => setCustomer(e.target.value)}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }} />
                            <Button variant="outlined"
                                    startIcon={<IconSeeker icon="square-plus" />}
                                    onClick={() => { brandFormData.value = { brandID: null, color: generateHexColor(), customerID: null } }}>
                                New Brand
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                {cid && (
                    <Grid item xs={12}>
                        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
                            <Tab label="Product" />
                            <Tab label="Visibility" />
                        </Tabs>
                    </Grid>
                )}

                <Grid item xs={12}>
                    {!cid ? (
                        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                            <IconSeeker icon="tags" sx={{ fontSize: 48, mb: 1 }} />
                            <Typography>Select a customer to review and fix its brands.</Typography>
                        </Box>
                    ) : tab === 0 ? (
                        // The `key` is load-bearing: both panels sit in the same slot of
                        // the tree and hold a different number of hooks, so without it
                        // React reuses the fiber across the tab switch and a useState
                        // lands on another hook's slot ("Should have a queue").
                        <ProductBrandsPanel key="product" cid={cid} site={site} onSiteChange={setSite} />
                    ) : (
                        <VisibilityBrandsPanel key="visibility" cid={cid} site={site} onSiteChange={setSite} />
                    )}
                </Grid>
            </Grid>

            {/* Master-data brand creation popup (same as the Brands dashboard) */}
            <AdminBrandsForm />
        </PageWrapper>
    )
}
